/* Rulers along the top and left of the canvas, in the gangsheet's units,
   measured from the sheet's top-left corner. The sheet's span is tinted and
   the selection's span is marked, so sizes and positions read at a glance. */

import { useEffect, useRef } from 'react';
import type { Unit } from '@gsb/core';
import type { View } from './SheetCanvas';

export const RULER = 22;

const MM_STEPS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
const IN_STEPS = [25.4 / 16, 25.4 / 8, 25.4 / 4, 25.4 / 2, 25.4, 50.8, 127, 254, 508, 1270];

function steps(unit: Unit, scale: number) {
  const list = unit === 'in' ? IN_STEPS : MM_STEPS;
  const major = list.find(s => s * scale >= 64) ?? list[list.length - 1];
  const div = unit === 'in' ? 4 : major >= 10 && String(major).startsWith('2') ? 4 : 5;   // 20 → 4 × 5, 50 → 5 × 10
  const minor = major / div;
  return { major, minor: minor * scale >= 5 ? minor : null };
}

function label(mm: number, unit: Unit) {
  const v = unit === 'in' ? mm / 25.4 : unit === 'cm' ? mm / 10 : mm;
  const r = Math.round(v * 100) / 100;
  return String(Number.isInteger(r) ? r : r.toFixed(unit === 'in' ? 2 : 1).replace(/0+$/, '').replace(/\.$/, ''));
}

interface Props {
  view: View;
  size: { w: number; h: number };
  unit: Unit;
  sheet: { widthMm: number; lengthMm: number };
  sel: { x: number; y: number; w: number; h: number } | null;
}

export function Rulers({ view, size, unit, sheet, sel }: Props) {
  const top = useRef<HTMLCanvasElement>(null);
  const left = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const dpr = window.devicePixelRatio || 1;
    const { major, minor } = steps(unit, view.scale);
    const draw = (c: HTMLCanvasElement | null, horizontal: boolean) => {
      if (!c) return;
      const len = horizontal ? size.w - RULER : size.h - RULER;
      if (len <= 0) return;
      const W = horizontal ? len : RULER, H = horizontal ? RULER : len;
      c.width = Math.round(W * dpr); c.height = Math.round(H * dpr);
      const x = c.getContext('2d')!;
      x.scale(dpr, dpr);
      x.fillStyle = '#F8FBFC'; x.fillRect(0, 0, W, H);
      const off = (horizontal ? view.x : view.y) - RULER;       // screen position of world 0, in ruler pixels
      const toS = (mm: number) => off + mm * view.scale;
      // the sheet's span, then the selection's
      const span = horizontal ? sheet.widthMm : sheet.lengthMm;
      const fill = (a: number, b: number, col: string) => {
        const s0 = Math.max(0, toS(a)), s1 = Math.min(len, toS(b));
        if (s1 <= s0) return;
        x.fillStyle = col;
        if (horizontal) x.fillRect(s0, 0, s1 - s0, H); else x.fillRect(0, s0, W, s1 - s0);
      };
      fill(0, span, 'rgba(17,197,217,.07)');
      if (sel) fill(horizontal ? sel.x : sel.y, horizontal ? sel.x + sel.w : sel.y + sel.h, 'rgba(17,197,217,.28)');
      // ticks
      const w0 = -off / view.scale, w1 = (len - off) / view.scale;
      x.strokeStyle = '#9AAAB5'; x.lineWidth = 1;
      x.fillStyle = '#52606D';
      x.font = '600 10px Montserrat, Arial, sans-serif';
      x.textBaseline = 'top';
      x.beginPath();
      if (minor) {
        for (let m = Math.floor(w0 / minor) * minor; m <= w1; m += minor) {
          const p = Math.round(toS(m)) + 0.5;
          if (horizontal) { x.moveTo(p, H - 5); x.lineTo(p, H); } else { x.moveTo(W - 5, p); x.lineTo(W, p); }
        }
      }
      for (let m = Math.floor(w0 / major) * major; m <= w1; m += major) {
        const p = Math.round(toS(m)) + 0.5;
        if (horizontal) { x.moveTo(p, H - 11); x.lineTo(p, H); } else { x.moveTo(W - 11, p); x.lineTo(W, p); }
      }
      x.stroke();
      for (let m = Math.floor(w0 / major) * major; m <= w1; m += major) {
        const p = toS(m);
        const t = label(m, unit);
        if (horizontal) x.fillText(t, p + 3, 3);
        else { x.save(); x.translate(3, p + 3); x.rotate(Math.PI / 2); x.translate(0, -10); x.fillText(t, 0, 0); x.restore(); }
      }
      // edge line
      x.fillStyle = '#DCE8EC';
      if (horizontal) x.fillRect(0, H - 1, W, 1); else x.fillRect(W - 1, 0, 1, H);
    };
    draw(top.current, true);
    draw(left.current, false);
  }, [view, size.w, size.h, unit, sheet.widthMm, sheet.lengthMm, sel?.x, sel?.y, sel?.w, sel?.h]);
  return (
    <>
      <canvas ref={top} className="ruler ruler-top" style={{ left: RULER, width: Math.max(0, size.w - RULER), height: RULER }} aria-hidden="true" />
      <canvas ref={left} className="ruler ruler-left" style={{ top: RULER, width: RULER, height: Math.max(0, size.h - RULER) }} aria-hidden="true" />
      <span className="ruler-corner" aria-hidden="true">{unit}</span>
    </>
  );
}
